import { Box, Button, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutline from '@mui/icons-material/DeleteOutlined';
import { EmptyState } from '../../components/EmptyState';
import { KeyValueList } from '../../components/KeyValueList';
import { Panel } from '../../components/Panel';
import { SrbCode } from '../../components/SrbCode';
import { StatusChip } from '../../components/StatusChip';
import { toneColor } from '../../components/tone';
import { addDays, daysBetween, dmy } from '../../lib/dates';
import { statusOf, TYPE_TONE, type ReminderItem } from './model';

/** The reminders from today to a week away */
export function UpcomingPanel({
  items,
  today,
  selectedId,
  onSelect,
}: {
  items: ReminderItem[];
  today: string;
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  const end = addDays(today, 7);
  const next = items.filter((r) => !r.complete && (r.date < today || (r.date >= today && r.date <= end)));
  return (
    <Panel title="Upcoming (Next 7 Days)" count={next.length}>
      {next.length ? (
        <Stack spacing={1.25}>
          {next.map((r) => {
            const s = statusOf(r, today);
            return (
              <Stack
                key={r.id}
                direction="row"
                spacing={1.25}
                onClick={() => onSelect(r.id)}
                sx={{
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 0.5,
                  borderRadius: 1.5,
                  bgcolor: r.id === selectedId ? 'action.hover' : undefined,
                }}
              >
                <Stack sx={{ width: 34, alignItems: 'center', lineHeight: 1 }}>
                  <Typography sx={{ fontWeight: 750, fontSize: 18, lineHeight: 1 }}>
                    {Number(r.date.slice(8))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(r.date + 'T00:00:00Z').toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' })}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    bgcolor: toneColor(TYPE_TONE[r.type]),
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{r.srb || 'Reminder'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.title}
                  </Typography>
                </Box>
                <StatusChip label={s.label} tone={s.tone} />
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <EmptyState>Nothing in the next 7 days.</EmptyState>
      )}
    </Panel>
  );
}

type DetailsProps = {
  r?: ReminderItem;
  today: string;
  busy: boolean;
  onComplete: (r: ReminderItem) => void;
  onDelete: (r: ReminderItem) => void;
};

/** The chosen reminder in full, with Mark as Complete and Delete */
export function DetailsPanel({ r, today, busy, onComplete, onDelete }: DetailsProps) {
  if (!r) {
    return (
      <Panel title="Reminder Details">
        <EmptyState>Pick a reminder to see it here.</EmptyState>
      </Panel>
    );
  }
  const s = statusOf(r, today);
  return (
    <Panel title="Reminder Details">
      <KeyValueList
        rows={[
          { label: 'Title', value: r.title },
          { label: 'Type', value: r.type },
          {
            label: 'Date',
            value: `${dmy(r.date)} (${daysBetween(today, r.date) === 0 ? 'today' : `${Math.abs(daysBetween(today, r.date))} days ${daysBetween(today, r.date) < 0 ? 'ago' : 'away'}`})`,
          },
          { label: 'Time', value: r.time || '–' },
          { label: 'Related To', value: r.relatedTo },
          { label: 'Created By', value: r.createdBy },
          { label: 'Description', value: r.description || '–' },
        ]}
      />
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Shipment
        </Typography>
        {r.srb ? <SrbCode code={r.srb} /> : <span>–</span>}
        <Box sx={{ flex: 1 }} />
        <StatusChip label={s.label} tone={s.tone} />
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CheckIcon />}
          disabled={busy || r.complete}
          onClick={() => onComplete(r)}
        >
          Mark as Complete
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<DeleteOutline />}
          disabled={busy}
          onClick={() => onDelete(r)}
        >
          Delete
        </Button>
      </Stack>
    </Panel>
  );
}
